import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, Form, Input, Button, Typography, Alert, Spin } from 'antd';
import { LockOutlined, UserOutlined, MailOutlined, KeyOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import BrandLogo from '@/components/common/BrandLogo';
import { useAuth } from '@/contexts/AuthContext';
import { acceptInvitation } from '@/api/users';

const { Title, Text } = Typography;

interface InvitationData {
  userId: string;
  userEmail: string;
  userDisplayName: string;
  workspace: {
    id: string;
    name: string;
    slug: string;
  };
  role: string;
  status: string;
  expiresAt: Date;
  inviter: {
    displayName: string;
  };
}

export default function InviteAccept() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { firestoreUser, loading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  /** Fatal: bad token, expired invite, Firestore fetch failure — full-page only */
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Form / account creation errors — inline above the form, not a false “full page” error */
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setLoadError('No invitation token provided');
      setLoading(false);
      return;
    }

    fetchInvitation();
  }, [token]);

  // Wait for user data to be fully loaded after account creation
  useEffect(() => {
    if (accountCreated && !authLoading && firestoreUser) {
      navigate('/dashboard');
    }
  }, [accountCreated, authLoading, firestoreUser, navigate]);

  const fetchInvitation = async () => {
    if (!token) {
      setLoadError('No invitation token provided');
      setLoading(false);
      return;
    }

    try {
      const invitationRef = doc(db, 'invitations', token);
      const invitationDoc = await getDoc(invitationRef);
      
      if (!invitationDoc.exists()) {
        setLoadError('Invalid invitation token');
        setLoading(false);
        return;
      }

      const data = invitationDoc.data();
      
      if (data.expiresAt?.toDate() < new Date()) {
        setLoadError('This invitation has expired');
        setLoading(false);
        return;
      }

      if (data.status !== 'pending') {
        setLoadError('This invitation has already been used');
        setLoading(false);
        return;
      }

      setInvitation({
        userId: data.userId,
        userEmail: data.userEmail,
        userDisplayName: data.userDisplayName,
        workspace: data.workspace,
        role: data.role,
        status: data.status,
        expiresAt: data.expiresAt.toDate(),
        inviter: data.inviter
      });

      // Prefill the form
      form.setFieldsValue({
        email: data.userEmail,
        displayName: data.userDisplayName
      });

      setLoading(false);
    } catch (error) {
      console.error('Error fetching invitation:', error);
      setLoadError('Failed to load invitation');
      setLoading(false);
    }
  };

  const handleSubmit = async (values: { email: string; displayName: string; password: string; confirmPassword: string }) => {
    if (values.password !== values.confirmPassword) {
      setSubmitError('Passwords do not match');
      return;
    }

    if (!invitation) {
      setSubmitError('No invitation data available');
      return;
    }

    if (!token) {
      setSubmitError('No invitation token available');
      return;
    }

    setCreating(true);
    setSubmitError(null);

    try {
      await acceptInvitation(token, {
        email: values.email,
        displayName: values.displayName,
        password: values.password
      });

      setAccountCreated(true);
      setCreating(false);
    } catch (error: any) {
      console.error('Error creating account:', error);
      if (error.code === 'auth/email-already-in-use') {
        setSubmitError('An account with this email already exists. Please sign in instead.');
      } else if (error.code === 'auth/weak-password') {
        setSubmitError('Password is too weak. Please choose a stronger password.');
      } else {
        setSubmitError(error.message || 'Failed to create account. Please try again.');
      }
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <Spin size="large" tip="Loading invitation…" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        padding: '20px'
      }}>
        <Card style={{ maxWidth: 400, width: '100%' }}>
          <Alert
            message="Invitation Error"
            description={loadError}
            type="error"
            showIcon
          />
          <Button 
            type="primary" 
            onClick={() => navigate('/login')}
            style={{ marginTop: 16 }}
          >
            Go to Login
          </Button>
        </Card>
      </div>
    );
  }

  if (!invitation) {
    return null;
  }

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      background: '#f0f2f5',
      padding: '20px'
    }}>
      <Card style={{ maxWidth: 500, width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <BrandLogo variant="bright" showTagline={true} />
          <Title level={2} style={{ marginTop: 16 }}>Accept Invitation</Title>
          <Text type="secondary">
            You've been invited to join <strong>{invitation.workspace.name}</strong>
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Invited by {invitation.inviter.displayName}
          </Text>
        </div>

        {!accountCreated ? (
          <>
            {submitError ? (
              <Alert
                type="error"
                showIcon
                message={submitError}
                style={{ marginBottom: 16 }}
                closable
                onClose={() => setSubmitError(null)}
              />
            ) : null}
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              autoComplete="off"
            >
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="Email"
                  disabled
                  style={{ backgroundColor: '#f5f5f5' }}
                />
              </Form.Item>

              <Form.Item
                name="displayName"
                label="Display Name"
                rules={[
                  { required: true, message: 'Please enter your display name' }
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Display Name"
                  style={{ backgroundColor: '#ffffff' }}
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: 'Please enter a password' },
                  { min: 8, message: 'Password must be at least 8 characters' }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Password"
                />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirm Password"
                rules={[
                  { required: true, message: 'Please confirm your password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<KeyOutlined />}
                  placeholder="Confirm Password"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={creating}
                  style={{ width: '100%' }}
                  size="large"
                >
                  Create Account
                </Button>
              </Form.Item>
            </Form>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Text type="secondary">
                Already have an account?{' '}
                <Button type="link" onClick={() => navigate('/login')}>
                  Sign In
                </Button>
              </Text>
            </div>
          </>
        ) : null}
      </Card>

      {/* Success State - Show when account is created */}
      {accountCreated && (
        <Card style={{ maxWidth: 500, width: '100%', marginTop: 16 }}>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ marginBottom: 16 }}>
              <CheckCircleOutlined 
                style={{ 
                  fontSize: 48, 
                  color: '#52c41a',
                  marginBottom: 16 
                }} 
              />
            </div>
            <Title level={3} style={{ color: '#52c41a', marginBottom: 8 }}>
              Account Created Successfully!
            </Title>
            <Text type="secondary" style={{ fontSize: 16, marginBottom: 24 }}>
              Welcome to <strong>{invitation.workspace.name}</strong>!
            </Text>
            <div style={{ marginBottom: 16 }}>
              <Spin size="large" />
            </div>
            <Text type="secondary" style={{ marginBottom: 8 }}>
              Setting up your account...
            </Text>
            {authLoading && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Loading user data...
              </Text>
            )}
            {!authLoading && firestoreUser && (
              <Text type="secondary" style={{ fontSize: 12, color: '#52c41a' }}>
                Account ready! Redirecting...
              </Text>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
